/**
 * Deterministic fingerprint for the "is this search already saved?" check.
 * The same normalized inputs always produce the same string, independent
 * of object key order, so we can compare a live SearchPage param set
 * against rows fetched from the saved-searches BFF without a server hop.
 */

type SearchParams = {
  keywords?: string | null;
  pub_categoryLevel1?: string | null;
  pub_condition?: string | null;
  price?: string | null;
  sort?: string | null;
  distance?: string | null;
  address?: string | null;
  origin?: string | null;
  bounds?: string | null;
};

type SavedSearchShape = {
  query_text: string;
  filters_json: Record<string, unknown> | null | undefined;
  category_filter: string[] | null | undefined;
};

const norm = (v: unknown): string => (typeof v === 'string' ? v.trim().toLowerCase() : '');

/**
 * Canonical string signature for a SearchPage param set. Stable across
 * key ordering and empty-value variations so the same search produces
 * the same signature whether it came from the URL or the BFF.
 *
 * @param p SearchPage params snapshot from `useSearchPage().params`
 * @returns signature string (canonical, sorted-key JSON-ish form)
 */
export const paramsToSignature = (p: SearchParams): string => {
  const parts: Record<string, string> = {
    keywords: norm(p.keywords),
    pub_categoryLevel1: norm(p.pub_categoryLevel1),
    pub_condition: norm(p.pub_condition),
    price: norm(p.price),
    sort: norm(p.sort),
    distance: norm(p.distance),
    address: norm(p.address),
    origin: norm(p.origin),
    bounds: norm(p.bounds),
  };
  return Object.keys(parts)
    .sort()
    .map(k => `${k}=${parts[k]}`)
    .join('&');
};

/**
 * Canonical signature for a SavedSearch row from the BFF. Inverts the
 * mapping used in `CustomSearchPage.handleSaveSearch` (filters_json +
 * category_filter[] + query_text) so the comparison is symmetric.
 */
export const savedSearchToSignature = (s: SavedSearchShape): string => {
  const f = (s.filters_json || {}) as Record<string, unknown>;
  return paramsToSignature({
    keywords: s.query_text,
    pub_categoryLevel1: s.category_filter?.[0] ?? '',
    pub_condition: typeof f.pub_condition === 'string' ? f.pub_condition : '',
    price: typeof f.price === 'string' ? f.price : '',
    sort: typeof f.sort === 'string' ? f.sort : '',
    distance: typeof f.distance === 'string' ? f.distance : '',
    address: typeof f.address === 'string' ? f.address : '',
    origin: typeof f.origin === 'string' ? f.origin : '',
    bounds: typeof f.bounds === 'string' ? f.bounds : '',
  });
};
